import { useCallback, useEffect, useRef } from 'react';
import { DWELLERS } from '../../data/dwellers';
import { ROOM_CENTERS, FLOOR_BOTTOMS } from '../../game/pathfinding';
import type { SpriteKey } from '../../types';
import { FRAME_W, FRAME_H, getDwellerFrames } from './pixelDweller';

// World = bunker-bg.png pixels, 1:1 (same convention as BunkerScene).
const WORLD_W = 887;
const WORLD_H = 1774;
const SPRITE_SCALE = 3; // matches BunkerScene SPRITE_SCALE
const WALK_SPEED = 66; // world px/s
const WALK_FRAME_MS = 110;
const BREATH_MS = 480;
const BREATH_CYCLE = [0, 1, 0, 3];

interface SimDweller {
  id: SpriteKey;
  x: number;
  floor: number;
  dir: 1 | -1;
  walking: boolean;
  targetX: number;
  walkFrame: number;
  breathStep: number;
  blinkUntil: number;
  nextBlinkAt: number;
}

function roomsOnFloor(floor: number): Array<{ x: number }> {
  return Object.values(ROOM_CENTERS).filter((r) => r.floor === floor);
}

export interface MiniBunkerSimProps {
  /** Dweller drawn with a selection bracket, if any. */
  selectedId?: SpriteKey | null;
  /** Fired when a sprite is clicked. Clicks on empty bunker do nothing. */
  onSelect?: (id: SpriteKey) => void;
}

/**
 * Live miniature of the actual bunker: the real background art with the ten
 * real dweller sprites idling, blinking, and occasionally walking to a
 * neighbouring room. Only one dweller moves at a time — the bunker stays
 * calm, exactly like the game. Static under prefers-reduced-motion.
 *
 * Sprites are clickable. Hit-testing runs against the live simulation rather
 * than fixed hotspots, because dwellers stroll: a hotspot pinned to a room
 * centre would drift off its dweller within the first minute. Keyboard and
 * screen-reader users select agents through the panel's own controls — the
 * canvas is aria-hidden and never the only route to an agent.
 */
export default function MiniBunkerSim({ selectedId = null, onSelect }: MiniBunkerSimProps = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  /** Live sim state, exposed to the pointer handlers outside the raf closure. */
  const dwellersRef = useRef<SimDweller[]>([]);
  const selectedRef = useRef<SpriteKey | null>(selectedId);
  const drawRef = useRef<((now: number) => void) | null>(null);

  /** Pointer → world coords → the dweller under it, or null. */
  const hitTest = useCallback((clientX: number, clientY: number): SpriteKey | null => {
    const wrap = wrapRef.current;
    if (!wrap) return null;
    const rect = wrap.getBoundingClientRect();
    if (rect.width === 0) return null;
    const scale = rect.width / WORLD_W;
    const wx = (clientX - rect.left) / scale;
    const wy = (clientY - rect.top) / scale;
    const halfW = (FRAME_W * SPRITE_SCALE) / 2;
    const h = FRAME_H * SPRITE_SCALE;
    // Back to front: later dwellers draw on top, so they win a tie.
    for (let i = dwellersRef.current.length - 1; i >= 0; i--) {
      const d = dwellersRef.current[i];
      const top = FLOOR_BOTTOMS[d.floor] - h + 4;
      if (wx >= d.x - halfW && wx <= d.x + halfW && wy >= top && wy <= top + h) return d.id;
    }
    return null;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;

    const dwellers: SimDweller[] = DWELLERS.map((d) => {
      const center = ROOM_CENTERS[d.room];
      return {
        id: d.id as SpriteKey,
        x: center.x,
        floor: center.floor,
        dir: 1,
        walking: false,
        targetX: center.x,
        walkFrame: 0,
        breathStep: 0,
        blinkUntil: 0,
        nextBlinkAt: performance.now() + 2000 + Math.random() * 5000,
      };
    });
    dwellersRef.current = dwellers;

    const draw = (now: number): void => {
      ctx.clearRect(0, 0, WORLD_W, WORLD_H);
      const sel = selectedRef.current;
      if (sel) {
        const d = dwellers.find((x) => x.id === sel);
        if (d) {
          const h = FRAME_H * SPRITE_SCALE;
          const halfW = (FRAME_W * SPRITE_SCALE) / 2;
          const top = FLOOR_BOTTOMS[d.floor] - h + 4;
          ctx.save();
          ctx.strokeStyle = '#86d46f';
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 4]);
          ctx.strokeRect(d.x - halfW - 4, top - 4, halfW * 2 + 8, h + 8);
          ctx.restore();
        }
      }
      for (const d of dwellers) {
        const frames = getDwellerFrames(d.id);
        let frame: HTMLCanvasElement;
        if (d.walking) {
          frame = frames.walk[d.walkFrame % frames.walk.length];
        } else if (now < d.blinkUntil) {
          frame = frames.idle[2]; // blink frame
        } else {
          frame = frames.idle[BREATH_CYCLE[d.breathStep % BREATH_CYCLE.length]];
        }
        const feetY = FLOOR_BOTTOMS[d.floor];
        const top = feetY - FRAME_H * SPRITE_SCALE + 4;
        ctx.save();
        ctx.translate(d.x, top);
        ctx.scale(d.dir * SPRITE_SCALE, SPRITE_SCALE);
        ctx.drawImage(frame, -FRAME_W / 2, 0);
        ctx.restore();
      }
    };

    drawRef.current = draw;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      draw(performance.now());
      return () => { drawRef.current = null; };
    }

    let raf = 0;
    let lastTick = performance.now();
    let lastBreath = lastTick;
    let lastWalkFrame = lastTick;
    let nextWalkAt = lastTick + 2500;

    const tick = (now: number): void => {
      const dt = Math.min(0.1, (now - lastTick) / 1000);
      lastTick = now;

      // Schedule a stroll: one dweller at a time keeps the bunker calm.
      const someoneWalking = dwellers.some((d) => d.walking);
      if (!someoneWalking && now >= nextWalkAt) {
        const d = dwellers[Math.floor(Math.random() * dwellers.length)];
        const others = roomsOnFloor(d.floor).filter((r) => Math.abs(r.x - d.x) > 40);
        const target = others.length > 0
          ? others[Math.floor(Math.random() * others.length)].x + (Math.random() * 60 - 30)
          : d.x + (Math.random() < 0.5 ? -90 : 90);
        d.targetX = Math.max(60, Math.min(720, target));
        d.walking = true;
        d.dir = d.targetX >= d.x ? 1 : -1;
        nextWalkAt = now + 4000 + Math.random() * 4000;
      }

      for (const d of dwellers) {
        if (!d.walking) {
          if (now >= d.nextBlinkAt) {
            d.blinkUntil = now + 160;
            d.nextBlinkAt = now + 2500 + Math.random() * 5000;
          }
          continue;
        }
        const step = WALK_SPEED * dt * d.dir;
        d.x += step;
        if ((d.dir === 1 && d.x >= d.targetX) || (d.dir === -1 && d.x <= d.targetX)) {
          d.x = d.targetX;
          d.walking = false;
        }
      }

      if (now - lastWalkFrame >= WALK_FRAME_MS) {
        lastWalkFrame = now;
        for (const d of dwellers) if (d.walking) d.walkFrame++;
      }
      if (now - lastBreath >= BREATH_MS) {
        lastBreath = now;
        for (const d of dwellers) if (!d.walking) d.breathStep++;
      }

      draw(now);
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); drawRef.current = null; };
  }, []);

  // The raf loop reads selectedRef every frame, so the animated path picks a
  // new selection up on its own; the explicit repaint is what keeps the
  // reduced-motion path (which drew exactly once) in sync.
  useEffect(() => {
    selectedRef.current = selectedId;
    drawRef.current?.(performance.now());
  }, [selectedId]);

  const interactive = Boolean(onSelect);

  return (
    <div
      ref={wrapRef}
      onClick={interactive ? (e) => { const id = hitTest(e.clientX, e.clientY); if (id) onSelect?.(id); } : undefined}
      onPointerMove={interactive ? (e) => {
        const el = e.currentTarget;
        el.style.cursor = hitTest(e.clientX, e.clientY) ? 'pointer' : 'default';
      } : undefined}
      style={{ position: 'relative', width: '100%' }}
    >
      {/* The hero's largest asset. The PNG is 2.1 MB — far too heavy to gate a
          landing page on — so browsers that support WebP get a 390 KB q92
          encode instead (visually identical: mean delta 2.4/255). The PNG stays
          as the fallback and remains the source of truth for the Phaser scene. */}
      <picture>
        <source srcSet="/bunker/bunker-bg.webp" type="image/webp" />
        <img
          src="/bunker/bunker-bg.png"
          alt="Cross-section of the FOUNDR-XI bunker: five floors of rooms, one per AI agent"
          width={WORLD_W}
          height={WORLD_H}
          style={{ display: 'block', width: '100%', height: 'auto', imageRendering: 'pixelated' }}
        />
      </picture>
      <canvas
        ref={canvasRef}
        width={WORLD_W}
        height={WORLD_H}
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', imageRendering: 'pixelated' }}
      />
    </div>
  );
}
